import { PaginationControls } from "@/components/ui/pagination-controls";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TablePrimitive,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { cn } from "@/lib/class-control";
import { TableBuilderProps } from "./types";
import { useTableState } from "./use-table-state";
import { TableActions } from "./table-actions";
import { TableFilterDialog } from "./table-filter-dialog";
import { MobileCard } from "./mobile-card";
import InputSearch from "../ui/input-search";
import { Dispatch, SetStateAction } from "react";

/**
 * TableBuilder - Componente genérico de tabela com busca, filtros e paginação.
 *
 * @example
 * ```tsx
 * <TableBuilder
 *   data={users}
 *   columns={[
 *     { key: "name", label: "Nome" },
 *     { key: "email", label: "Email" },
 *   ]}
 *   rowKey="id"
 *   actions={{
 *     onView: (user) => console.log(user),
 *     onEdit: (user) => console.log(user),
 *   }}
 * />
 * ```
 */
export function TableBuilder<T extends Record<string, unknown>>({
  data,
  columns,
  rowKey,
  isLoading = false,
  actions,
  filters,
  itemsPerPage = 10,
  showSearch = true,
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nenhum dado encontrado",
  headerSlot,
  mobileCardRenderer,
  className,
  titleSubtitle,
}: TableBuilderProps<T>) {
  const tableState = useTableState({
    data,
    columns,
    filters,
    itemsPerPage,
  });

  const {
    searchTerm,
    setSearchTerm,
    activeFilters,
    setFilter,
    clearFilters,
    currentPage,
    setCurrentPage,
    filteredData,
    paginatedData,
    totalPages,
  } = tableState;

  // Handler para aplicar filtros do dialog
  const handleApplyFilters = (newFilters: Record<string, string>) => {
    Object.entries(newFilters).forEach(([key, value]) => {
      setFilter(key, value);
    });
  };

  // Renderizar loading skeleton
  if (isLoading) {
    return (
      <div className={cn("space-y-4", className)}>
        <div className="flex gap-4">
          <Skeleton className="h-10 w-64 bg-[#15203c]" />
          <Skeleton className="h-10 w-24 bg-[#15203c]" />
        </div>
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full bg-[#15203c]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header: Search, Filters, Custom Slot */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="">
          {titleSubtitle?.title && (
            <>
              <h2 className="text-lg font-medium text-white mb-1">
                {titleSubtitle.title}
              </h2>
              {titleSubtitle?.subtitle && (
                <p className="text-sm text-[#888888]">
                  {titleSubtitle.subtitle}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          {showSearch && (
            <>
              <InputSearch
                positionIcon="end"
                value={searchTerm}
                onChange={setSearchTerm as Dispatch<SetStateAction<string>>}
                focus="none"
                className="placeholder:text-[#f6f6f6]"
              />

              {filters && filters.length > 0 && (
                <TableFilterDialog
                  filters={filters}
                  activeFilters={activeFilters}
                  onApply={handleApplyFilters}
                  onClear={clearFilters}
                />
              )}

              {headerSlot && <div>{headerSlot}</div>}
            </>
          )}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block">
        <TablePrimitive>
          <TableHeader>
            <TableRow hoverable={false}>
              {columns.map((column) => (
                <TableHead
                  key={String(column.key)}
                  align={column.align}
                  className={column.width}
                >
                  {column.label}
                </TableHead>
              ))}
              {actions && <TableHead align="center">Ações</TableHead>}
            </TableRow>
          </TableHeader>

          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow hoverable={false}>
                <TableCell
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="text-center text-gray-400 py-8"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => (
                <TableRow key={String(row[rowKey])} hoverable>
                  {columns.map((column) => {
                    const value = row[column.key as keyof T];
                    const displayValue = column.render
                      ? column.render(value, row, index)
                      : value != null
                        ? String(value)
                        : "-";

                    return (
                      <TableCell
                        key={String(column.key)}
                        align={column.align}
                        className={column.width}
                      >
                        {displayValue}
                      </TableCell>
                    );
                  })}

                  {actions && (
                    <TableCell align="center">
                      <TableActions row={row} actions={actions} />
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </TablePrimitive>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {paginatedData.length === 0 ? (
          <div className="text-center text-gray-400 py-8">{emptyMessage}</div>
        ) : (
          paginatedData.map((row) =>
            mobileCardRenderer ? (
              <div key={String(row[rowKey])}>{mobileCardRenderer(row, actions)}</div>
            ) : (
              <MobileCard
                key={String(row[rowKey])}
                row={row}
                columns={columns}
                actions={actions}
                rowKey={rowKey}
              />
            )
          )
        )}
      </div>

      {/* Pagination */}
      {filteredData.length > itemsPerPage && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={filteredData.length}
        />
      )}
    </div>
  );
}
